import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as actions from '.';
import * as api from '../../../services/messagePackage/checkout';
import {
  getCurrentStep,
  getSelectedPhysicians,
  getSelectedProductsByPhysician,
  getUserSubscriptionId,
} from './selectors';
import { ProductByPhysician } from '../../types';
import {
  Physician,
  SubscribeData,
  SubscribeSummaryData,
} from '../../../services/types';

type PayloadBestProduct = ReturnType<
  typeof actions.fetchBestProductByPhysician
>;
type PayloadSubmitProductSelection = ReturnType<
  typeof actions.submitProductSelection
>;

export function* fetchPhysiciansAvailableWorker() {
  try {
    const { data: availablePhysiciansArray } = yield call(
      api.getPhysiciansAvailable,
    );

    yield put(
      actions.fetchPhysiciansAvailableSuccess(availablePhysiciansArray),
    );
  } catch {
    yield put(actions.fetchPhysiciansAvailableFailure());
  }
}

export function* fetchBestProductByPhysicianWorker({
  payload: { kinds, physicianId },
}: PayloadBestProduct) {
  try {
    yield put(actions.fetchBestProductByPhysicianStart());

    const { data: bestProduct } = yield call(api.getBestProduct, {
      kinds,
      physicianId,
    });

    yield put(actions.fetchBestProductByPhysicianSuccess(bestProduct));
  } catch {
    yield put(actions.fetchBestProductByPhysicianFailure());
  }
}

export function* submitProductSelectionWorker({
  payload: productByPhysician,
}: PayloadSubmitProductSelection) {
  const currentSelectedProductByPhysicians: ProductByPhysician[] = yield select(
    getSelectedProductsByPhysician,
  );
  const selectedPhysicians: Physician[] = yield select(getSelectedPhysicians);
  const currentStep: number = yield select(getCurrentStep);

  const stepsCount = selectedPhysicians.length;
  const selectedProductByPhysicians = [...currentSelectedProductByPhysicians];
  const index = currentSelectedProductByPhysicians.findIndex(
    (item) => item.id === productByPhysician.id,
  );

  if (index >= 0) {
    selectedProductByPhysicians[index] = productByPhysician;
  } else {
    selectedProductByPhysicians.push(productByPhysician);
  }

  yield put(actions.setSelectedProductByPhysician(selectedProductByPhysicians));

  if (stepsCount >= 2 && currentStep < stepsCount) {
    yield put(actions.setCurrentStep(currentStep + 1));
  } else {
    yield put(actions.toggleOpenSummary());
    yield put(actions.fetchSubscriptionSummary());
  }
}

export function* fetchSubscriptionSummaryWorker() {
  try {
    yield put(actions.fetchSubscriptionSummaryStart());

    const subscriptionId: string = yield select(getUserSubscriptionId);
    const selectedProductsByPhisician: ProductByPhysician[] = yield select(
      getSelectedProductsByPhysician,
    );

    const subscribeSummaryData = selectedProductsByPhisician.reduce(
      (summaryData, productByPhysician) => {
        const newSummaryData = { ...summaryData };

        if (newSummaryData.products[productByPhysician.productSlug]) {
          newSummaryData.products[productByPhysician.productSlug] += 1;
        } else {
          newSummaryData.products = {
            ...newSummaryData.products,
            [productByPhysician.productSlug]: 1,
          };
        }

        return newSummaryData;
      },
      { products: {}, typeHiring: 'upgrade' } as SubscribeSummaryData,
    );

    const { data } = yield call(api.getSubscriptionSummary, {
      subscriptionId,
      subscribeSummaryData,
    });

    yield put(actions.fetchSubscriptionSummarySuccess(data.data));
  } catch {
    yield put(actions.fetchSubscriptionSummaryFailure());
  }
}

export function* submitSubscriptionWorker() {
  try {
    yield put(actions.submitSubscriptionStart());
    const selectedProductsByPhisician: ProductByPhysician[] = yield select(
      getSelectedProductsByPhysician,
    );

    const subscriptionUpgradeData = selectedProductsByPhisician.reduce(
      (subscribeData, productByPhysician) => {
        const newSubscribeData = { ...subscribeData };
        const { id, kinds, productSlug } = productByPhysician;

        if (!newSubscribeData[productSlug]) {
          return {
            ...newSubscribeData,
            [productSlug]: {
              [id]: kinds,
            },
          };
        }

        newSubscribeData[productSlug] = {
          ...newSubscribeData[productSlug],
          [id]: kinds,
        };

        return newSubscribeData;
      },
      {} as SubscribeData,
    );

    yield call(api.postSubscriptionUpgrade, subscriptionUpgradeData);

    yield put(actions.toggleOpenSummary());
    yield put(actions.toggleOpenSuccess());
    yield put(actions.submitSubscriptionSuccess());
  } catch {
    yield put(actions.submitSubscriptionFailure());
  }
}

export default function* messagingSelectPhysiciansSagas() {
  yield all([
    takeLatest(
      actions.fetchPhysiciansAvailable,
      fetchPhysiciansAvailableWorker,
    ),
    takeLatest(
      actions.fetchBestProductByPhysician,
      fetchBestProductByPhysicianWorker,
    ),
    takeLatest(actions.submitProductSelection, submitProductSelectionWorker),
    takeLatest(
      actions.fetchSubscriptionSummary,
      fetchSubscriptionSummaryWorker,
    ),
    takeLatest(actions.submitSubscription, submitSubscriptionWorker),
  ]);
}
